import { useState, useCallback, useEffect, type CSSProperties } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { APP } from './tokens';
import { AppFolderIcon } from './AppFolderIcon';

// ─── Shared styles ──────────────────────────────

const MODAL_SHELL: CSSProperties = {
  borderRadius: 12,
  overflow: 'clip',
  backgroundColor: APP.bgCardBody,
  border: `1px solid ${APP.border}`,
  fontFamily: APP.fontFamily,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
};

const HEADER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  paddingTop: 12,
  paddingRight: 14,
  paddingBottom: 10,
  paddingLeft: 14,
  gap: 8,
  borderBottom: '1px solid #F0EDE7',
};

const HEADER_TITLE: CSSProperties = {
  flex: 1,
  fontSize: 14,
  fontWeight: 600,
  color: APP.textPrimary,
  lineHeight: '16px',
};

const SEARCH_BAR: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  borderRadius: 6,
  paddingBlock: 6,
  paddingInline: 10,
  gap: 6,
  backgroundColor: '#F5F2EC',
};

const SEARCH_INPUT: CSSProperties = {
  flex: 1,
  border: 'none',
  background: 'none',
  outline: 'none',
  fontSize: 13,
  fontWeight: 400,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
  lineHeight: '14px',
};

const ACTION_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 7,
  paddingBlock: 9,
  paddingInline: 16,
  gap: 6,
  backgroundColor: APP.bgExportAction,
  border: 'none',
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
  fontSize: 14,
  fontWeight: 600,
  color: '#FFFFFF',
  lineHeight: '16px',
  width: '100%',
};

const FORM_BODY: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingBlock: 10,
  paddingInline: 12,
  gap: 6,
};

const FIELD_LABEL: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: '#9B8E83',
  lineHeight: '12px',
};

const TEXT_INPUT: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  borderRadius: 6,
  paddingBlock: 7,
  paddingInline: 10,
  backgroundColor: '#F5F2EC',
  border: 'none',
  outline: 'none',
  fontSize: 13,
  fontWeight: 400,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
  lineHeight: '14px',
  width: '100%',
  boxSizing: 'border-box' as const,
};

const TEXTAREA_STYLE: CSSProperties = {
  ...TEXT_INPUT,
  minHeight: 58,
  resize: 'none' as const,
  paddingBlock: 8,
};

const FOOTER: CSSProperties = {
  paddingBlock: 10,
  paddingInline: 12,
};

const CLOSE_BTN: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

// ─── Icons ──────────────────────────────────────

function CloseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M3 3L9 9M9 3L3 9" stroke="#9B8E83" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="4.5" cy="4.5" r="3" stroke="#9B8E83" strokeWidth="1.2" />
      <path d="M7 7L9 9" stroke="#9B8E83" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="7" height="10" viewBox="0 0 7 10" fill="none" style={{ flexShrink: 0 }}>
      <path d="M1 1L5 5L1 9" stroke="#9B8E83" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
      <path d="M4.5 1.5L3 10.5M9 1.5L7.5 10.5M1 4H11M1 8H11" stroke="#9B8E83" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="12" height="10" viewBox="0 0 12 10" fill="none" style={{ flexShrink: 0 }}>
      <path d="M1 2L6 5.5L11 2M1 1H11V9H1V1Z" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="7" cy="7" r="7" fill="#D97645" />
      <path d="M4 7L6 9.5L10 4.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Dropbox / Drive Folder Browser ─────────────

interface FolderItem {
  id: string;
  name: string;
  hasChildren?: boolean;
}

export interface FolderBrowserModalProps {
  service: 'dropbox' | 'drive';
  onSave: (folderId: string, folderName: string) => void;
  onClose: () => void;
  getToken?: () => string | null;
  fileId?: string;
}

export function FolderBrowserModal({ service, onSave, onClose, getToken, fileId }: FolderBrowserModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [breadcrumb, setBreadcrumb] = useState<string[]>(
    service === 'dropbox' ? ['All Files'] : ['My Drive']
  );
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch folders from API
  useEffect(() => {
    const token = getToken?.();
    if (!token) {
      setFolders([]);
      return;
    }
    setLoadingFolders(true);
    const endpoint = service === 'dropbox'
      ? `/api/integrations/dropbox/documents?path=${encodeURIComponent(currentPath)}&foldersOnly=true`
      : `/api/integrations/google-drive/files?parentId=${encodeURIComponent(currentPath === '/' ? 'root' : currentPath)}`;
    axios
      .get(endpoint, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        const items = (data.documents ?? data.files ?? data.results ?? []) as Array<{
          id?: string; _id?: string; path?: string; name: string; type?: string; mimeType?: string;
        }>;
        setFolders(
          items
            .filter((item) => {
              if (service === 'dropbox') return item.type === 'folder' || !item.type;
              return item.mimeType === 'application/vnd.google-apps.folder' || !item.mimeType;
            })
            .map((item) => ({
              id: item.id || item._id || item.path || item.name,
              name: item.name,
              hasChildren: true,
            }))
        );
      })
      .catch(() => {
        setFolders([]);
      })
      .finally(() => setLoadingFolders(false));
  }, [service, currentPath, getToken]);

  const navigateInto = (folder: FolderItem) => {
    const newPath = service === 'dropbox'
      ? `${currentPath === '/' ? '' : currentPath}/${folder.name}`
      : folder.id;
    setCurrentPath(newPath);
    setBreadcrumb((prev) => [...prev, folder.name]);
    setSelectedFolder(null);
  };

  const handleSave = async () => {
    const folderName = folders.find((f) => f.id === selectedFolder)?.name ?? breadcrumb[breadcrumb.length - 1];
    const token = getToken?.();

    if (service === 'dropbox' && token && fileId) {
      setSaving(true);
      try {
        const destPath = selectedFolder
          ? `${currentPath === '/' ? '' : currentPath}/${folders.find((f) => f.id === selectedFolder)?.name ?? ''}`
          : currentPath;
        await axios.post(
          '/api/integrations/dropbox/export',
          { fileId, destinationPath: destPath },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        onSave(selectedFolder ?? currentPath, folderName);
      } catch {
        toast({ title: 'Export failed', description: 'Could not save to Dropbox.', variant: 'destructive' });
      } finally {
        setSaving(false);
      }
      return;
    }

    if (service === 'drive' && token && fileId) {
      setSaving(true);
      try {
        await axios.post(
          '/api/integrations/google-drive/export',
          { fileId, folderId: selectedFolder || undefined, fileName: undefined },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        onSave(selectedFolder ?? '', folderName);
      } catch {
        toast({ title: 'Export failed', description: 'Could not save to Google Drive.', variant: 'destructive' });
      } finally {
        setSaving(false);
      }
      return;
    }

    // Fallback for unauthenticated
    onSave(selectedFolder ?? '', folderName);
  };

  const logoSrc = service === 'dropbox'
    ? '/IntPointsLogos/dropbox-2-logo-png-transparent.png'
    : '/IntPointsLogos/Google_Drive_icon_(2020).svg.png';

  const title = service === 'dropbox' ? 'Save to Dropbox' : 'Save to Drive';

  const filtered = searchQuery
    ? folders.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : folders;

  return (
    <div style={{ ...MODAL_SHELL, width: 240 }}>
      {/* Header */}
      <div style={HEADER}>
        <img src={logoSrc} alt="" width={16} height={16} style={{ flexShrink: 0, objectFit: 'contain' }} />
        <span style={HEADER_TITLE}>{title}</span>
        <button type="button" onClick={onClose} style={CLOSE_BTN}><CloseIcon /></button>
      </div>

      {/* Search */}
      <div style={{ margin: '0 10px 8px', ...SEARCH_BAR }}>
        <SearchIcon />
        <input
          type="text"
          placeholder="Search folders…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ ...SEARCH_INPUT, fontSize: 12 }}
        />
      </div>

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', paddingInline: 14, paddingBottom: 6, gap: 4 }}>
        {breadcrumb.map((crumb, i) => (
          <span key={crumb} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {i > 0 && <span style={{ fontSize: 11, color: '#9B8E83' }}>›</span>}
            <span style={{
              fontSize: 11,
              fontWeight: i === breadcrumb.length - 1 ? 600 : 400,
              color: i === breadcrumb.length - 1 ? APP.textPrimary : '#9B8E83',
              fontFamily: APP.fontFamily,
            }}>
              {crumb}
            </span>
          </span>
        ))}
      </div>

      {/* Folder list */}
      <div style={{ display: 'flex', flexDirection: 'column', paddingInline: 6, gap: 1 }}>
        {loadingFolders ? (
          <div style={{ padding: '12px 8px', fontSize: 12, color: '#9B8E83', fontFamily: APP.fontFamily }}>Loading folders…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '12px 8px', fontSize: 12, color: '#9B8E83', fontFamily: APP.fontFamily }}>
            {!getToken?.() ? 'Sign in and connect your account to browse folders' : 'No folders found'}
          </div>
        ) : filtered.map((folder) => {
          const isSelected = selectedFolder === folder.id;
          return (
            <button
              key={folder.id}
              type="button"
              onClick={() => setSelectedFolder(folder.id)}
              onDoubleClick={() => folder.hasChildren && navigateInto(folder)}
              style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 6,
                paddingBlock: 7,
                paddingInline: 8,
                gap: 8,
                backgroundColor: isSelected ? '#F0DDD5' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: APP.fontFamily,
              }}
            >
              <AppFolderIcon width={16} height={13} />
              <span style={{
                flex: 1,
                fontSize: 13,
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? '#D97645' : APP.textPrimary,
                lineHeight: '16px',
                textAlign: 'left',
              }}>
                {folder.name}
              </span>
              {isSelected && (
                <span style={{ fontSize: 11, color: '#9B8E83', fontWeight: 400 }}>selected</span>
              )}
              {!isSelected && folder.hasChildren && <ChevronRight />}
            </button>
          );
        })}
      </div>

      {/* Divider + Save button */}
      <div style={{ height: 1, backgroundColor: '#F0EDE7', marginTop: 8 }} />
      <div style={FOOTER}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{ ...ACTION_BTN, opacity: saving ? 0.6 : 1 }}
        >
          {saving ? 'Saving…' : 'Save here'}
        </button>
      </div>
    </div>
  );
}

// ─── Slack Channel Picker ───────────────────────

interface SlackChannel {
  id: string;
  name: string;
  memberCount?: number;
  suggested?: boolean;
}

export interface SlackExportModalProps {
  onSend: (channelId: string, channelName: string, message?: string) => void;
  onClose: () => void;
  getToken?: () => string | null;
}

export function SlackExportModal({ onSend, onClose, getToken }: SlackExportModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [message, setMessage] = useState('');
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [sending, setSending] = useState(false);

  // Fetch real channels
  useEffect(() => {
    const token = getToken?.();
    if (!token) {
      setChannels([]);
      return;
    }
    setLoadingChannels(true);
    axios
      .get('/api/integrations/slack/channels', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        const results = (data.channels ?? []) as Array<{ id: string; name: string; memberCount?: number }>;
        setChannels(results.map((ch) => ({ id: ch.id, name: ch.name, memberCount: ch.memberCount })));
        if (results.length > 0) setSelectedChannel(results[0].id);
      })
      .catch(() => {
        setChannels([]);
      })
      .finally(() => setLoadingChannels(false));
  }, [getToken]);

  const filtered = searchQuery
    ? channels.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : channels;

  const selectedName = channels.find((c) => c.id === selectedChannel)?.name ?? 'channel';

  return (
    <div style={{ ...MODAL_SHELL, width: 260 }}>
      {/* Header */}
      <div style={HEADER}>
        <img src="/IntPointsLogos/Slack_icon_2019.svg.png" alt="" width={16} height={16} style={{ flexShrink: 0, objectFit: 'contain' }} />
        <span style={HEADER_TITLE}>Share to Slack</span>
        <button type="button" onClick={onClose} style={CLOSE_BTN}><CloseIcon /></button>
      </div>

      {/* Search */}
      <div style={{ margin: '0 10px 8px', ...SEARCH_BAR }}>
        <HashIcon />
        <input
          type="text"
          placeholder="Search channels…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ ...SEARCH_INPUT, fontSize: 12 }}
        />
      </div>

      {/* Suggested label */}
      <div style={{ paddingInline: 14, paddingBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 500, color: '#9B8E83', fontFamily: APP.fontFamily }}>Suggested</span>
      </div>

      {/* Channel list */}
      <div style={{ display: 'flex', flexDirection: 'column', paddingInline: 6, gap: 1 }}>
        {loadingChannels ? (
          <div style={{ padding: '12px 8px', fontSize: 12, color: '#9B8E83', fontFamily: APP.fontFamily }}>Loading channels…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '12px 8px', fontSize: 12, color: '#9B8E83', fontFamily: APP.fontFamily }}>
            {!getToken?.() ? 'Sign in and connect Slack to see channels' : 'No channels found'}
          </div>
        ) : filtered.map((channel) => {
          const isSelected = selectedChannel === channel.id;
          return (
            <button
              key={channel.id}
              type="button"
              onClick={() => setSelectedChannel(channel.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 6,
                paddingBlock: 7,
                paddingInline: 8,
                gap: 6,
                backgroundColor: isSelected ? '#F0DDD5' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: APP.fontFamily,
              }}
            >
              <span style={{ fontSize: 13, color: '#9B8E83', fontWeight: 500 }}>#</span>
              <span style={{
                flex: 1,
                fontSize: 13,
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? '#D97645' : APP.textPrimary,
                lineHeight: '16px',
                textAlign: 'left',
              }}>
                {channel.name}
              </span>
              {isSelected && <CheckIcon />}
              {!isSelected && channel.memberCount && (
                <span style={{ fontSize: 11, color: '#9B8E83' }}>{channel.memberCount} members</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Divider */}
      <div style={{ height: 1, backgroundColor: '#F0EDE7', marginTop: 4 }} />

      {/* Message textarea */}
      <div style={{ paddingBlock: 8, paddingInline: 10 }}>
        <textarea
          placeholder="Add a message (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          style={{ ...TEXTAREA_STYLE, minHeight: 44 }}
        />
      </div>

      {/* Send button */}
      <div style={FOOTER}>
        <button
          type="button"
          disabled={!selectedChannel || sending}
          onClick={async () => {
            const token = getToken?.();
            if (!token || !selectedChannel) {
              toast({ title: 'Not connected', description: 'Sign in and connect Slack to send messages.', variant: 'destructive' });
              return;
            }
            setSending(true);
            try {
              const text = message.trim() || `Shared from Corner`;
              await axios.post(
                '/api/integrations/slack/send',
                { channelId: selectedChannel, text },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              onSend(selectedChannel, selectedName, message || undefined);
            } catch {
              toast({ title: 'Send failed', description: 'Could not send message to Slack.', variant: 'destructive' });
            } finally {
              setSending(false);
            }
          }}
          style={{ ...ACTION_BTN, opacity: !selectedChannel || sending ? 0.5 : 1 }}
        >
          {sending ? 'Sending…' : `Send to #${selectedName}`}
        </button>
      </div>
    </div>
  );
}

// ─── Gmail Composer ─────────────────────────────

export interface GmailExportModalProps {
  fileName: string;
  onSend: (to: string[], subject: string, message?: string) => void;
  onClose: () => void;
  getToken?: () => string | null;
  fileId?: string;
}

export function GmailExportModal({ fileName, onSend, onClose, getToken, fileId }: GmailExportModalProps) {
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [toInput, setToInput] = useState('');
  const [subject, setSubject] = useState(fileName);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const addEmail = useCallback(() => {
    const email = toInput.trim();
    if (email && email.includes('@') && !toEmails.includes(email)) {
      setToEmails((prev) => [...prev, email]);
      setToInput('');
    }
  }, [toInput, toEmails]);

  const removeEmail = (email: string) => {
    setToEmails((prev) => prev.filter((e) => e !== email));
  };

  return (
    <div style={{ ...MODAL_SHELL, width: 280 }}>
      {/* Header */}
      <div style={HEADER}>
        <img src="/IntPointsLogos/Gmail_icon_(2020).svg.png" alt="" width={16} height={16} style={{ flexShrink: 0, objectFit: 'contain' }} />
        <span style={HEADER_TITLE}>Send via Gmail</span>
        <button type="button" onClick={onClose} style={CLOSE_BTN}><CloseIcon /></button>
      </div>

      {/* Form body */}
      <div style={FORM_BODY}>
        {/* TO field */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={FIELD_LABEL}>To</span>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 4,
            borderRadius: 6,
            paddingBlock: 5,
            paddingInline: 8,
            backgroundColor: '#F5F2EC',
            minHeight: 34,
          }}>
            {toEmails.map((email) => (
              <span key={email} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                borderRadius: 4,
                paddingBlock: 2,
                paddingInline: 6,
                backgroundColor: '#D97645',
                fontSize: 12,
                fontWeight: 500,
                color: '#FFFFFF',
                fontFamily: APP.fontFamily,
              }}>
                {email}
                <button
                  type="button"
                  onClick={() => removeEmail(email)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
                >
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path d="M2 2L6 6M6 2L2 6" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            ))}
            <input
              type="email"
              placeholder={toEmails.length === 0 ? 'Add email…' : 'Add more…'}
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addEmail();
                }
              }}
              onBlur={addEmail}
              style={{
                flex: 1,
                minWidth: 60,
                border: 'none',
                background: 'none',
                outline: 'none',
                fontSize: 12,
                color: APP.textPrimary,
                fontFamily: APP.fontFamily,
              }}
            />
          </div>
        </div>

        {/* SUBJECT field */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={FIELD_LABEL}>Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={TEXT_INPUT}
          />
        </div>

        {/* File badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          borderRadius: 5,
          paddingBlock: 5,
          paddingInline: 8,
          backgroundColor: '#FDF0EA',
          border: '1px solid #F0DDD5',
          alignSelf: 'flex-start',
        }}>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            borderRadius: 3,
            backgroundColor: '#C13B2A',
            fontSize: 6,
            fontWeight: 700,
            color: '#FFFFFF',
            fontFamily: APP.fontFamily,
          }}>
            PDF
          </span>
          <span style={{ fontSize: 12, fontWeight: 500, color: APP.textPrimary, fontFamily: APP.fontFamily }}>
            {fileName}
          </span>
        </div>

        {/* MESSAGE field */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={FIELD_LABEL}>Message</span>
          <textarea
            placeholder="Write a message (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={TEXTAREA_STYLE}
          />
        </div>
      </div>

      {/* Divider + Send button */}
      <div style={{ height: 1, backgroundColor: '#F0EDE7' }} />
      <div style={FOOTER}>
        <button
          type="button"
          onClick={async () => {
            const token = getToken?.();
            if (!token || !fileId) {
              toast({ title: 'Not connected', description: 'Sign in and connect Gmail to send emails.', variant: 'destructive' });
              return;
            }
            setSending(true);
            try {
              await axios.post(
                '/api/integrations/gmail/send',
                {
                  to: toEmails,
                  subject,
                  textBody: message || undefined,
                  fileIds: [fileId],
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              onSend(toEmails, subject, message || undefined);
            } catch {
              toast({ title: 'Send failed', description: 'Could not send email via Gmail.', variant: 'destructive' });
            } finally {
              setSending(false);
            }
          }}
          disabled={toEmails.length === 0 || sending}
          style={{
            ...ACTION_BTN,
            opacity: toEmails.length === 0 || sending ? 0.5 : 1,
            cursor: toEmails.length === 0 || sending ? 'not-allowed' : 'pointer',
          }}
        >
          <MailIcon />
          {sending ? 'Sending…' : 'Send email'}
        </button>
      </div>
    </div>
  );
}

// ─── Notion Export (simplified) ──────────────────

export interface NotionExportModalProps {
  onSave: (targetId: string, targetName: string) => void;
  onClose: () => void;
  getToken?: () => string | null;
  title?: string;
  content?: string;
}

export function NotionExportModal({ onSave, onClose, getToken, title, content }: NotionExportModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [pages, setPages] = useState<{ id: string; name: string }[]>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = getToken?.();
    if (!token) {
      setPages([]);
      return;
    }
    setLoadingPages(true);
    axios
      .get('/api/integrations/notion/targets', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        const results = (data.results ?? []) as Array<{ id: string; title?: string; name?: string }>;
        setPages(results.map((r) => ({ id: r.id, name: r.title || r.name || 'Untitled' })));
      })
      .catch(() => {
        setPages([]);
      })
      .finally(() => setLoadingPages(false));
  }, [getToken]);

  const filtered = searchQuery
    ? pages.filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : pages;

  const selectedName = pages.find((p) => p.id === selected)?.name ?? 'page';

  const handleSave = async () => {
    if (!selected) return;
    const token = getToken?.();
    if (token && title) {
      setSaving(true);
      try {
        await axios.post(
          '/api/integrations/notion/page',
          {
            parentPageId: selected,
            title,
            children: content ? [{ type: 'paragraph', text: content }] : [],
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        onSave(selected, selectedName);
      } catch {
        toast({ title: 'Export failed', description: 'Could not save to Notion.', variant: 'destructive' });
      } finally {
        setSaving(false);
      }
      return;
    }
    onSave(selected, selectedName);
  };

  return (
    <div style={{ ...MODAL_SHELL, width: 240 }}>
      {/* Header */}
      <div style={HEADER}>
        <img src="/IntPointsLogos/notion-icon.webp" alt="" width={16} height={16} style={{ flexShrink: 0, objectFit: 'contain' }} />
        <span style={HEADER_TITLE}>Save to Notion</span>
        <button type="button" onClick={onClose} style={CLOSE_BTN}><CloseIcon /></button>
      </div>

      {/* Search */}
      <div style={{ margin: '0 10px 8px', ...SEARCH_BAR }}>
        <SearchIcon />
        <input
          type="text"
          placeholder="Search pages…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ ...SEARCH_INPUT, fontSize: 12 }}
        />
      </div>

      {/* Page list */}
      <div style={{ display: 'flex', flexDirection: 'column', paddingInline: 6, gap: 1 }}>
        {loadingPages ? (
          <div style={{ padding: '12px 8px', fontSize: 12, color: '#9B8E83', fontFamily: APP.fontFamily }}>Loading pages…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '12px 8px', fontSize: 12, color: '#9B8E83', fontFamily: APP.fontFamily }}>
            {!getToken?.() ? 'Sign in and connect Notion to see pages' : 'No pages found'}
          </div>
        ) : filtered.map((page) => {
          const isSelected = selected === page.id;
          return (
            <button
              key={page.id}
              type="button"
              onClick={() => setSelected(page.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 6,
                paddingBlock: 7,
                paddingInline: 8,
                gap: 8,
                backgroundColor: isSelected ? '#F0DDD5' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: APP.fontFamily,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                <rect x="2" y="1" width="10" height="12" rx="1.5" stroke="#9B8E83" strokeWidth="1.2" fill="none" />
                <line x1="5" y1="5" x2="9" y2="5" stroke="#9B8E83" strokeWidth="1" strokeLinecap="round" />
                <line x1="5" y1="7.5" x2="9" y2="7.5" stroke="#9B8E83" strokeWidth="1" strokeLinecap="round" />
                <line x1="5" y1="10" x2="7.5" y2="10" stroke="#9B8E83" strokeWidth="1" strokeLinecap="round" />
              </svg>
              <span style={{
                flex: 1,
                fontSize: 13,
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? '#D97645' : APP.textPrimary,
                lineHeight: '16px',
                textAlign: 'left',
              }}>
                {page.name}
              </span>
              {isSelected && <CheckIcon />}
            </button>
          );
        })}
      </div>

      {/* Divider + Save button */}
      <div style={{ height: 1, backgroundColor: '#F0EDE7', marginTop: 8 }} />
      <div style={FOOTER}>
        <button
          type="button"
          onClick={handleSave}
          disabled={!selected || saving}
          style={{
            ...ACTION_BTN,
            opacity: selected && !saving ? 1 : 0.5,
            cursor: selected && !saving ? 'pointer' : 'not-allowed',
          }}
        >
          {saving ? 'Saving…' : `Save to ${selectedName}`}
        </button>
      </div>
    </div>
  );
}
